/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import Spacer from '../../ui/Spacer';
import { keyColors } from '../../../styles/colors/index';
import DotComponent from '../../ui/DotComponent';
import Picture from '../../ui/Picture';
import { Container, Row, Column } from '../../../styles/Grid';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import { fieldHasValue } from '../../../utils/sitecore';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { withNamespaces } from 'react-i18next';
import compose from 'lodash/fp/compose';
import { TEMPLATES_IDS } from '../../../utils/constants';
import {
  containerMarginCss,
  headline,
  description,
  amenityItem,
  amenityList,
  amenityListLine,
  columnContainer,
  grayContainer,
  previewImgContainer,
  listPreview,
  itemPreview,
  textPreview,
  previewContainer,
  columnContainerCss,
  iconContainerCss,
  eyebrow,
  previewImageStyleImage,
  previewImageStyleIcon,
  changeDirectionCss,
} from './index.styles';

export const defaultData = {
  AmenitiesUpperText: {
    value: '',
  },
  AmenitiesHeadline: {
    value: '',
  },
  AmenitiesBodyText: {
    value: '',
  },
  AmenitiesList: [
    {
      fields: {
        AmenityName: {
          value: '',
        },
        Image: {
          value: {
            src: '',
            alt: '',
          },
        },
        Icon: {
          value: {
            src: '',
            alt: '',
          },
        },
      },
    },
  ],
  ImageLeft: {
    value: false,
  },
};

const previewImageSizes = {
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    gravity: 'auto:subject',
    aspectRatio: '4:3',
    width: 171,
    height: 128,
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    gravity: 'auto:subject',
    aspectRatio: '4:3',
    width: 171,
    height: 128,
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    gravity: 'auto:subject',
    aspectRatio: '4:3',
    width: 171,
    height: 128,
  },
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    gravity: 'auto:subject',
    aspectRatio: '4:3',
    width: 171,
    height: 128,
  },
};

const iconImageSizes = {
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 18,
    height: 18,
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 18,
    height: 18,
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 18,
    height: 18,
  },
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 18,
    height: 18,
  },
};

const NUMBER_OF_IMAGES = 4;

const chunkAmenties = amenities => {
  const previewAmenities = amenities.slice(0, NUMBER_OF_IMAGES);
  const restAmenities = amenities.slice(NUMBER_OF_IMAGES);
  return [previewAmenities, restAmenities];
};

const GrayCards = ({ amenity }) => {
  return (
    <div css={grayContainer}>
      <div css={previewImgContainer}>
        <Picture
          image={amenity.fields.Image}
          imageSizes={previewImageSizes}
          pictureCss={previewImageStyleImage}
          editable={false}
        />
      </div>
      <p css={textPreview}>
        <Text field={amenity.fields.AmenityName} editable={false} />
      </p>
    </div>
  );
};

export class Amenities extends SafePureComponent {
  safeRender() {
    const { props } = this;
    const { t } = props;
    const fields = defaultsDeep(props.fields, defaultData);
    const ImageLeftValue = get(fields, 'ImageLeft.value', false);
    const amenities = get(fields, 'AmenitiesList', []);
    const [previewAmenities, restAmenities] = chunkAmenties(amenities);
    const isEditing = get(props, 'sitecoreContext.pageEditing', false);
    const parentTemplate = get(
      props,
      'sitecoreContext.route.templateId',
      false,
    );

    const shouldRenderAmenitiesUpperText =
      isEditing || fieldHasValue(fields.AmenitiesUpperText);
    const shouldRenderAmenitiesBodyText =
      isEditing || fieldHasValue(fields.AmenitiesBodyText);
    const shouldRenderAmenitiesHeadline =
      isEditing || fieldHasValue(fields.AmenitiesHeadline);
    const getAmenityHeadline =
      (parentTemplate && parentTemplate.toLowerCase()) ===
      TEMPLATES_IDS.locationTemplateId ? (
        t('AmenitiesHeadline')
      ) : (
        <Text field={fields.AmenitiesHeadline} />
      );

    return (
      <Spacer
        backgroundColor={keyColors.transparent}
        marginCss={containerMarginCss}
        containerProps={{ 'data-sticky-id': get(props, 'rendering.uid') }}
      >
        <Container>
          {shouldRenderAmenitiesUpperText && (
            <Row>
              <Column offsets={{ lg: ImageLeftValue && 6 }}>
                <p css={eyebrow}>
                  <Text field={fields.AmenitiesUpperText} />
                </p>
              </Column>
            </Row>
          )}
          <Row css={ImageLeftValue && changeDirectionCss}>
            <Column
              cols={{ sm: 6, md: 8, lg: 6 }}
              offsets={{ lg: ImageLeftValue ? 1 : 0 }}
            >
              {shouldRenderAmenitiesHeadline && (
                <h2 css={headline}>
                  {getAmenityHeadline}
                  <DotComponent />
                </h2>
              )}
              {shouldRenderAmenitiesBodyText && (
                <Row>
                  <Column cols={{ sm: 6, md: [7, 8], lg: [5, 6] }}>
                    <p css={description}>
                      <Text field={fields.AmenitiesBodyText} />
                    </p>
                  </Column>
                </Row>
              )}
              <ul css={[amenityList, amenityListLine]}>
                <Row>
                  <Column css={columnContainer} cols={{ sm: 6, md: 8 }}>
                    {restAmenities.map((amenity, index) => (
                      <li css={amenityItem} key={`amenities-${index}`}>
                        <span css={iconContainerCss}>
                          <Picture
                            pictureCss={previewImageStyleIcon}
                            image={amenity.fields.Icon}
                            imageSizes={iconImageSizes}
                            editable={false}
                          />
                        </span>
                        <p>
                          <Text
                            field={amenity.fields.AmenityName}
                            editable={false}
                          />
                        </p>
                      </li>
                    ))}
                  </Column>
                </Row>
              </ul>
            </Column>
            <Column
              cols={{ sm: 6, md: 6, lg: 5 }}
              offsets={{ md: 2, lg: ImageLeftValue ? 0 : 1 }}
            >
              <ul css={previewContainer}>
                <Row css={listPreview}>
                  {previewAmenities.map((amenity, index) => (
                    <Column
                      css={columnContainerCss}
                      key={`amenities-preview-${index}`}
                      cols={{ sm: [4.5, 6], md: [3, 6] }}
                    >
                      <li css={itemPreview}>
                        <GrayCards amenity={amenity} />
                      </li>
                    </Column>
                  ))}
                </Row>
              </ul>
            </Column>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

Amenities.displayName = 'Amenities';

Amenities.defaultProps = {
  fields: defaultData,
  t: key => key,
};

Amenities.propTypes = {
  fields: PropTypes.shape({
    AmenitiesUpperText: PropTypes.shape({
      value: PropTypes.string,
    }),
    AmenitiesBodyText: PropTypes.shape({
      value: PropTypes.string,
    }),
    AmenitiesHeadline: PropTypes.shape({
      value: PropTypes.string,
    }),
    AmenitiesList: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          AmenityName: PropTypes.shape({
            value: PropTypes.string,
          }),
          Image: PropTypes.shape({
            value: PropTypes.shape({
              src: PropTypes.string,
              alt: PropTypes.string,
            }),
          }),
          Icon: PropTypes.shape({
            value: PropTypes.shape({
              src: PropTypes.string,
              alt: PropTypes.string,
            }),
          }),
        }),
      }),
    ),
    ImageLeft: PropTypes.shape({
      value: PropTypes.bool,
    }),
  }),
};

export default compose(withNamespaces(), withSitecoreContext())(Amenities);
